import { LEARNING_CONTENT_SEED } from '../data/learningContentSeed';
import type { UserType } from '../types/user';
import type { JourneyPath } from '../types/journey';
import { prepareJourneyForLaunch, prepareJourneyList } from './courseLaunch';
import { contentPathToJourney, normalizeDbPath, type ContentPath } from './contentRecords';
import { readOfflineCourse } from './offlineCourse';
import { withPracticeFields } from './practiceCatalog';
import { supabase } from './supabaseClient';

const CATALOG_SELECT = `
  id, slug, title_my, title_en, summary_my, summary_en, body_my, body_en,
  duration, level, is_premium, launch_ready, primary_language, glossary, status, sort_order,
  cover_image_url, tags, rating, enrolled,
  learning_modules (
    id, path_id, slug, title_my, title_en, description_my, description_en,
    sort_order, status, is_premium,
    learning_lessons (
      id, path_id, module_id, slug, title_my, title_en, body_my, body_en,
      intro_my, intro_en, takeaways_my, takeaways_en, skill_my, skill_en,
      lesson_type, duration, sort_order, status, is_premium,
      youtube_video_id, video_url, tasks, deliverables, quiz, resources
    )
  )
`;

function finishDatabaseCatalog(paths: ContentPath[]): JourneyPath[] {
  return paths
    .filter((path) => path.status === 'published')
    .map((path) => prepareJourneyForLaunch(contentPathToJourney(path, true), path.slug, { trustSource: true }))
    .filter((path): path is JourneyPath => path !== null)
    .sort((a, b) => {
      if (Boolean(a.launchReady) !== Boolean(b.launchReady)) return a.launchReady ? -1 : 1;
      if (Boolean(a.isPremium) !== Boolean(b.isPremium)) return a.isPremium ? 1 : -1;
      return a.title.localeCompare(b.title);
    });
}

export function journeysFromSeed(): JourneyPath[] {
  return prepareJourneyList(LEARNING_CONTENT_SEED.map((path) => contentPathToJourney(path, true)));
}

async function loadPublishedPaths(): Promise<{ ok: boolean; paths: ContentPath[] }> {
  if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
    return { ok: false, paths: [] };
  }

  const { data, error } = await supabase.from('learning_paths').select(CATALOG_SELECT).eq('status', 'published');
  if (error) {
    console.warn('Learning content is not in Supabase yet. Using the bundled seed.', error.message);
    return { ok: false, paths: [] };
  }

  const paths = (data || []).map((row) => normalizeDbPath(row as unknown as Record<string, unknown>));
  if (paths.length === 0) {
    console.warn('No published learning paths in Supabase. Using the bundled seed.');
    return { ok: false, paths: [] };
  }
  const practice = await withPracticeFields(paths);
  return { ok: true, paths: practice.paths };
}

export async function fetchAllJourneyPaths(userType?: UserType): Promise<JourneyPath[]> {
  void userType;
  const loaded = await loadPublishedPaths();
  if (!loaded.ok) return journeysFromSeed();
  return finishDatabaseCatalog(loaded.paths);
}

export async function fetchJourneyPath(slug: string, userType?: UserType): Promise<JourneyPath | null> {
  void userType;
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false ? readOfflineCourse(slug) : null;
  if (offline) return offline;
  const loaded = await loadPublishedPaths();
  if (!loaded.ok) {
    const seeded = LEARNING_CONTENT_SEED.find((path) => path.slug === slug);
    return prepareJourneyForLaunch(seeded ? contentPathToJourney(seeded, true) : null, slug) || readOfflineCourse(slug);
  }
  const match = loaded.paths.find((path) => path.slug === slug) || null;
  return prepareJourneyForLaunch(match ? contentPathToJourney(match, true) : null, slug, { trustSource: true }) || readOfflineCourse(slug);
}

export async function fetchJourneyPaths(slugs: string[], userType?: UserType): Promise<JourneyPath[]> {
  const wanted = new Set(slugs);
  const paths = await fetchAllJourneyPaths(userType);
  return paths.filter((path) => wanted.has(path.slug));
}

export async function fetchAllLessons(userType?: UserType) {
  const paths = await fetchAllJourneyPaths(userType);
  return paths.flatMap((path) => path.modules.flatMap((module) => module.lessons || []));
}

export async function fetchAllQuickCourses(_userType?: UserType) {
  void _userType;
  return [];
}
